import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Header } from "@/components/site/Header";
import { WhatsAppFab } from "@/components/site/WhatsAppFab";
import { api, apiError } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { formatNaira } from "@/lib/format";
import { track } from "@/lib/analytics";

const VISA_TYPES = ["Tourist", "Business", "Family / Visit", "Student", "Conference / Event", "Medical", "Transit", "Other"];

const initialState = {
  customer_name: "",
  email: "",
  whatsapp: "",
  passenger_name: "",
  num_passengers: "1",
  departure: "",
  destination: "",
  departure_date: "",
  return_date: "",
  preferred_airline: "",
  preferred_departure_time: "",
  preferred_return_time: "",
  visa_country: "",
  visa_type: "",
  visa_appointment_date: "",
  service: "standard",
};

const Field = ({ label, id, required, children, hint }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium text-foreground">
      {label} {required && <span className="text-destructive">*</span>}
      {hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}
    </Label>
    {children}
  </div>
);

const SectionTitle = ({ step, children }) => (
  <div className="mb-5 flex items-center gap-3">
    <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-xs font-bold text-primary-foreground">{step}</span>
    <h2 className="font-heading text-lg font-semibold text-foreground">{children}</h2>
  </div>
);

const inputClass = "rounded-sm border-input focus:ring-2 focus:ring-primary focus:border-primary";

export default function Order() {
  const config = useConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState(initialState);
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startedTracked, setStartedTracked] = useState(false);

  useEffect(() => {
    const svc = params.get("service");
    if (svc && config.services.some((s) => s.id === svc)) {
      setForm((f) => ({ ...f, service: svc }));
    }
  }, [params, config.services]);

  const selected = useMemo(
    () => config.services.find((s) => s.id === form.service) || config.services[0],
    [config.services, form.service]
  );

  const set = (key) => (e) => {
    if (!startedTracked) {
      track("form_start");
      setStartedTracked(true);
    }
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const validate = () => {
    const required = [
      ["customer_name", "your full name"],
      ["email", "your email address"],
      ["whatsapp", "your WhatsApp number"],
      ["passenger_name", "the passenger name"],
      ["departure", "the departure city / airport"],
      ["destination", "the destination city / airport"],
      ["departure_date", "the departure date"],
      ["visa_country", "the country you are applying to"],
      ["visa_type", "the visa type"],
      ["visa_appointment_date", "your visa appointment date"],
    ];
    for (const [key, label] of required) {
      if (!String(form[key] || "").trim()) {
        toast.error(`Please provide ${label}.`);
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    if (!consent1 || !consent2) {
      toast.error("Please accept both confirmations before continuing.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    track("form_complete", { service: form.service });
    try {
      const payload = {
        ...form,
        num_passengers: parseInt(form.num_passengers, 10) || 1,
        return_date: form.return_date || null,
        preferred_airline: form.preferred_airline || null,
        preferred_departure_time: form.preferred_departure_time || null,
        preferred_return_time: form.preferred_return_time || null,
        visa_appointment_date: form.visa_appointment_date,
        consent_reservation: consent1,
        consent_accurate: consent2,
      };
      const { data: order } = await api.post("/orders", payload);
      track("payment_initiation", { order_number: order.order_number, service: form.service, amount: order.amount });

      const callback_url = `${window.location.origin}/payment/callback`;
      try {
        const { data: pay } = await api.post("/payments/initialize", { order_id: order.id, callback_url });
        window.location.href = pay.authorization_url;
      } catch (payErr) {
        const status = payErr?.response?.status;
        if (status === 503) {
          toast.error("Online payment is being set up. Your order details are saved — please reach us on WhatsApp to complete payment.", { duration: 8000 });
          navigate("/confirmation", {
            state: {
              order: {
                order_number: order.order_number,
                customer_name: form.customer_name,
                destination: form.destination,
                departure_date: form.departure_date,
                return_date: form.return_date,
                service: selected.name,
                processing_time: selected.processing_time,
                amount: order.amount,
                status: "PENDING PAYMENT",
              },
              pendingPayment: true,
            },
          });
        } else {
          toast.error(apiError(payErr?.response?.data?.detail, "Could not start payment. Please try again."));
        }
      }
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not submit your order. Please check your details."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-28 sm:pb-0">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Place your order</h1>
        <p className="mt-2 text-muted-foreground">Complete the details below. Your reservation is prepared after payment is confirmed.</p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]">
          {/* FORM */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
            {/* Customer */}
            <div className="rounded-sm border border-border bg-card p-6 lg:p-8">
              <SectionTitle step="1">Customer information</SectionTitle>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" id="customer_name" required>
                  <Input id="customer_name" data-testid="input-customer-name" className={inputClass} value={form.customer_name} onChange={set("customer_name")} />
                </Field>
                <Field label="Email address" id="email" required>
                  <Input id="email" type="email" data-testid="input-email" className={inputClass} value={form.email} onChange={set("email")} />
                </Field>
                <Field label="WhatsApp phone number" id="whatsapp" required>
                  <Input id="whatsapp" data-testid="input-whatsapp" placeholder="+234..." className={inputClass} value={form.whatsapp} onChange={set("whatsapp")} />
                </Field>
              </div>
            </div>

            {/* Passenger */}
            <div className="rounded-sm border border-border bg-card p-6 lg:p-8">
              <SectionTitle step="2">Passenger information</SectionTitle>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name exactly as on passport" id="passenger_name" required>
                  <Input id="passenger_name" data-testid="input-passenger-name" className={inputClass} value={form.passenger_name} onChange={set("passenger_name")} />
                </Field>
                <Field label="Number of passengers" id="num_passengers" required>
                  <Select value={form.num_passengers} onValueChange={set("num_passengers")}>
                    <SelectTrigger id="num_passengers" data-testid="select-passengers" className={inputClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            {/* Travel */}
            <div className="rounded-sm border border-border bg-card p-6 lg:p-8">
              <SectionTitle step="3">Travel information</SectionTitle>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Departure city / airport" id="departure" required>
                  <Input id="departure" data-testid="input-departure" placeholder="e.g. Lagos (LOS)" className={inputClass} value={form.departure} onChange={set("departure")} />
                </Field>
                <Field label="Destination city / airport" id="destination" required>
                  <Input id="destination" data-testid="input-destination" placeholder="e.g. London (LHR)" className={inputClass} value={form.destination} onChange={set("destination")} />
                </Field>
                <Field label="Intended departure date" id="departure_date" required>
                  <Input id="departure_date" type="date" data-testid="input-departure-date" className={inputClass} value={form.departure_date} onChange={set("departure_date")} />
                </Field>
                <Field label="Intended return date" id="return_date">
                  <Input id="return_date" type="date" data-testid="input-return-date" className={inputClass} value={form.return_date} onChange={set("return_date")} />
                </Field>
                <Field label="Preferred airline" id="preferred_airline" hint="(optional)">
                  <Input id="preferred_airline" data-testid="input-airline" className={inputClass} value={form.preferred_airline} onChange={set("preferred_airline")} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Preferred depart time" id="preferred_departure_time" hint="(optional)">
                    <Input id="preferred_departure_time" type="time" data-testid="input-depart-time" className={inputClass} value={form.preferred_departure_time} onChange={set("preferred_departure_time")} />
                  </Field>
                  <Field label="Preferred return time" id="preferred_return_time" hint="(optional)">
                    <Input id="preferred_return_time" type="time" data-testid="input-return-time" className={inputClass} value={form.preferred_return_time} onChange={set("preferred_return_time")} />
                  </Field>
                </div>
              </div>
            </div>

            {/* Visa */}
            <div className="rounded-sm border border-border bg-card p-6 lg:p-8">
              <SectionTitle step="4">Visa information</SectionTitle>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Country applying to" id="visa_country" required>
                  <Input id="visa_country" data-testid="input-visa-country" placeholder="e.g. United Kingdom" className={inputClass} value={form.visa_country} onChange={set("visa_country")} />
                </Field>
                <Field label="Visa type" id="visa_type" required>
                  <Select value={form.visa_type} onValueChange={set("visa_type")}>
                    <SelectTrigger id="visa_type" data-testid="select-visa-type" className={inputClass}>
                      <SelectValue placeholder="Select visa type" />
                    </SelectTrigger>
                    <SelectContent>
                      {VISA_TYPES.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Visa appointment date" id="visa_appointment_date" required>
                  <Input id="visa_appointment_date" type="date" data-testid="input-appointment-date" className={inputClass} value={form.visa_appointment_date} onChange={set("visa_appointment_date")} />
                </Field>
              </div>
            </div>

            {/* Service */}
            <div className="rounded-sm border border-border bg-card p-6 lg:p-8">
              <SectionTitle step="5">Choose your service</SectionTitle>
              <div className="grid gap-3">
                {config.services.map((svc) => {
                  const active = form.service === svc.id;
                  return (
                    <button
                      key={svc.id}
                      type="button"
                      data-testid={`service-option-${svc.id}`}
                      onClick={() => set("service")(svc.id)}
                      className={`flex items-center justify-between rounded-sm border p-4 text-left transition-colors ${
                        active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${active ? "border-primary" : "border-muted-foreground"}`}>
                          {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                        </span>
                        <div>
                          <p className="font-heading font-semibold text-foreground">{svc.name}</p>
                          <p className="text-sm text-muted-foreground">{svc.processing_time}</p>
                        </div>
                      </div>
                      <span className="font-heading text-lg font-bold text-foreground">{formatNaira(svc.amount)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Consents */}
            <div className="rounded-sm border border-border bg-card p-6 lg:p-8">
              <SectionTitle step="6">Confirmations</SectionTitle>
              <div className="space-y-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox data-testid="consent-reservation" checked={consent1} onCheckedChange={(v) => setConsent1(!!v)} className="mt-0.5 rounded-sm" />
                  <span className="text-sm leading-relaxed text-foreground">
                    I understand that VisaFlight provides a confirmed airline reservation and not a purchased airline ticket.
                    I understand that visa approval is not guaranteed.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox data-testid="consent-accurate" checked={consent2} onCheckedChange={(v) => setConsent2(!!v)} className="mt-0.5 rounded-sm" />
                  <span className="text-sm leading-relaxed text-foreground">
                    I confirm that the information I have provided is accurate.
                  </span>
                </label>
              </div>
            </div>
          </motion.div>

          {/* ORDER SUMMARY */}
          <div>
            <div className="lg:sticky lg:top-24">
              <div className="rounded-sm border border-border bg-card p-6 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-foreground">Order summary</h3>
                <dl className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Customer</dt>
                    <dd className="text-right font-medium text-foreground" data-testid="summary-customer">{form.customer_name || "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Destination</dt>
                    <dd className="text-right font-medium text-foreground" data-testid="summary-destination">{form.destination || "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Travel dates</dt>
                    <dd className="text-right font-medium text-foreground" data-testid="summary-dates">
                      {form.departure_date || "—"}{form.return_date ? ` → ${form.return_date}` : ""}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Service</dt>
                    <dd className="text-right font-medium text-foreground" data-testid="summary-service">{selected?.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Processing time</dt>
                    <dd className="text-right font-medium text-foreground" data-testid="summary-processing">{selected?.processing_time}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Included change</dt>
                    <dd className="text-right font-medium text-foreground">1 included</dd>
                  </div>
                </dl>
                <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                  <span className="font-heading text-base font-semibold text-foreground">Total</span>
                  <span className="font-heading text-2xl font-extrabold text-foreground" data-testid="summary-total">{formatNaira(selected?.amount)}</span>
                </div>

                <Button
                  data-testid="proceed-payment-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="mt-6 w-full rounded-sm bg-primary py-6 text-base font-semibold text-primary-foreground hover:bg-[#003DB3]"
                >
                  {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : <><Lock className="mr-2 h-4 w-4" /> Proceed to Secure Payment</>}
                </Button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" /> Payments secured by Paystack. Price is fixed and cannot be edited.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <WhatsAppFab showOrder={false} />
    </div>
  );
}
